import {
	OrderQuoteOfferStatus,
	useGetClientOrderQuoteOfferBatchesQuery,
	useRejectOrderQuoteOfferBatchMutation,
} from '@codezo/graphql';
import { FileText } from 'lucide-react';
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../../components/ui/button';
import { Card } from '../../../../components/ui/card';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../../../components/ui/dialog';
import { Field } from '../../../../components/ui/field';
import { Textarea } from '../../../../components/ui/textarea';
import { useQuoteCurrency } from '../context';
import {
	AcceptOffersModal,
	type MasterSelection,
	type OfferForAccept,
	type QuoteDeliveryAddress,
} from './AcceptOffersModal';
import type { RejectTarget } from './OfferStatusBadge';
import type { Batch, ClientQuoteItem } from './offer-types';
import type { ClientSelectionLine } from '../lib/useClientBatchSelection';
import { primaryProductImage } from '../../../../components/ui/product-image';

/* ── Types ── */

/**
 * What one batch card reports back: the plan it has taken and the line it has
 * settled on per product. Live state, not the debounced copy that is persisted —
 * accepting can follow a change by less than the save delay.
 */
interface ClientBatchCardState {
	selections: ClientSelectionLine[];
}

/**
 * Everything one batch card is handed. Named so a caller can supply a different
 * card without restating the list — see `renderBatch`.
 */
interface ClientBatchLayoutProps {
	batch: Batch;
	itemLookup: Map<string, ClientQuoteItem>;
	quoteDeadline?: string | null;
	onSelectionChange: (batchId: string, state: ClientBatchCardState) => void;
	onAccept?: () => void;
	onReject: (target: RejectTarget) => void;
}

interface QuoteOffersTabProps {
	quoteId: string;
	quoteName: string;
	quoteItems: ClientQuoteItem[];
	/** Quote-level deadline, the feasibility fallback when an item has none. */
	quoteDeadline?: string | null;
	/** The quote's current delivery address — what the accept flow starts from. */
	quoteDeliveryAddress?: QuoteDeliveryAddress | null;
	organizationId?: string | null;
	onQuoteChanged?: () => void;
	skip?: boolean;
	/**
	 * How to draw one batch.
	 *
	 * A render prop rather than the card inline: the batches query, the
	 * cross-batch accept flow and the reject dialog are the same whichever card
	 * is shown, and a second copy of this file is how two of them would start
	 * disagreeing about which offers are acceptable. It defaulted to the v1 card
	 * until that card went with combination plans; there is one caller now, and
	 * it passes its own. Mirrors `renderBatch` on the admin app's
	 * `OrderQuoteOffers`, for the same reason.
	 */
	renderBatch: (props: ClientBatchLayoutProps) => ReactNode;
	/**
	 * The files the offers carry, by quote line, reported up as the batches load.
	 *
	 * A client cannot read them any other way: `OrderQuoteItem.attachments` is
	 * admin-only and answers a client with an empty list, so the only copy that
	 * reaches this side rides on the offers — and only for batches already sent.
	 * The page wants them under the product they belong to, which is a section
	 * this tab doesn't own, hence the lift. Reported once per line: a batch can
	 * hold two offers on the same product, and both serve that line's one list.
	 */
	onAttachmentsChange?: (byItemId: Map<string, ClientItemAttachment[]>) => void;
}

/** One file on a quote line, as an offer serves it. */
export type ClientItemAttachment = Batch['offers'][number]['attachments'][number];

/* ════════════════════════ OFFERS TAB ════════════════════════ */

export function QuoteOffersTab({
	quoteId,
	quoteName,
	quoteItems,
	quoteDeadline,
	quoteDeliveryAddress,
	organizationId,
	onQuoteChanged,
	skip = false,
	renderBatch,
	onAttachmentsChange,
}: QuoteOffersTabProps) {
	const [showAcceptModal, setShowAcceptModal] = useState(false);
	const [rejectTarget, setRejectTarget] = useState<RejectTarget | null>(null);
	const [rejectReason, setRejectReason] = useState('');
	const selectedCurrency = useQuoteCurrency();
	const [rejectBatch, { loading: rejecting }] = useRejectOrderQuoteOfferBatchMutation();

	const { data, loading, refetch } = useGetClientOrderQuoteOfferBatchesQuery({
		variables: { orderQuoteId: quoteId, inCurrency: selectedCurrency },
		fetchPolicy: 'no-cache',
		skip,
	});

	const itemLookup = new Map(quoteItems.map((item) => [item.id, item]));
	const batches = data?.getClientOrderQuoteOfferBatches ?? [];

	/* Keyed on the query's own result rather than the derived map: the map is
	   rebuilt every render and would re-report forever. First offer to carry a
	   line wins — every offer on that line serves the same list.

	   Reported only when the files actually change, not on every refetch: a
	   rejection or an accept refetches these batches, and handing the page a new
	   Map each time would re-render the whole quote to say the same thing. */
	const reported = useRef('');
	useEffect(() => {
		if (!onAttachmentsChange) return;
		const byItemId = new Map<string, ClientItemAttachment[]>();
		for (const batch of data?.getClientOrderQuoteOfferBatches ?? []) {
			for (const offer of batch.offers) {
				if (offer.attachments.length > 0 && !byItemId.has(offer.orderQuoteItemId)) {
					byItemId.set(offer.orderQuoteItemId, offer.attachments);
				}
			}
		}
		const signature = [...byItemId]
			.map(([itemId, files]) => `${itemId}:${files.map((f) => f.id).join(',')}`)
			.sort()
			.join('|');
		if (signature === reported.current) return;
		reported.current = signature;
		onAttachmentsChange(byItemId);
	}, [data, onAttachmentsChange]);

	// Collect all pending offers across all batches for cross-batch accept
	const now = Date.now();
	const allPendingOffers: OfferForAccept[] = batches.flatMap((batch) => {
		const acceptDeadlinePassed = batch.acceptDeadline
			? new Date(batch.acceptDeadline).getTime() < now
			: false;
		return batch.offers
			.filter((o) => o.status === OrderQuoteOfferStatus.Pending)
			.map((offer) => {
				const item = itemLookup.get(offer.orderQuoteItemId);
				const img = primaryProductImage(item?.productImages);
				return {
					offerId: offer.id,
					orderQuoteItemId: offer.orderQuoteItemId,
					batchName: batch.name || 'Offer batch',
					productName: item?.productName || offer.name,
					productImage: img,
					pricingOptions: (offer.pricing ?? []).map((p) => {
						const qty = (item?.quantities ?? []).find(
							(q) => q.id === p.orderQuoteItemQuantityId
						);
						return {
							pricingId: p.id,
							quantity: qty?.quantity ?? 0,
							unitPrice: p.unitPrice ?? null,
							totalPrice: p.totalPrice ?? null,
							minProductionDays: p.minProductionDays ?? null,
							maxProductionDays: p.maxProductionDays ?? null,
							deliveries: (p.deliveries ?? []).map((d) => ({
								id: d.id,
								shippingMethod: d.shippingMethod,
								totalPrice: d.totalPrice ?? null,
								deliveryDaysMin: d.deliveryDaysMin,
								deliveryDaysMax: d.deliveryDaysMax,
								projectedDelivery: d.projectedDelivery ?? null,
								/* Carried so the review step can name a split as one:
								   its shippingMethod is derived from its first leg, and
								   a label built from that alone calls "20 Express, 80
								   Standard" plain Express. */
								isSplit: d.isSplit,
								parts: (d.parts ?? []).map((part) => ({
									sequence: part.sequence,
									shippingMethod: part.shippingMethod,
									quantity: part.quantity,
									/* Each leg's own arrival: the review step states one
									   line per leg, because a split's whole point is that
									   part of the order lands earlier. */
									projectedDelivery: part.projectedDelivery ?? null,
								})),
							})),
						};
					}),
					sample: offer.sample
						? {
								totalPrice: offer.sample.totalPrice ?? null,
								minProductionDays: offer.sample.minProductionDays ?? null,
								maxProductionDays: offer.sample.maxProductionDays ?? null,
								deliveryDaysMin: offer.sample.deliveryDaysMin ?? null,
								deliveryDaysMax: offer.sample.deliveryDaysMax ?? null,
								pricesByChoice: offer.sample.pricesByChoice,
							}
						: null,
					acceptDeadlinePassed,
				};
			});
	});
	const hasPendingOffers = allPendingOffers.length > 0;

	// Combinations the buyer committed to by downloading a quote PDF (per batch,
	// master only). The accept modal auto-selects these on open; a working (never
	// downloaded) selection is intentionally ignored — only a downloaded quote
	// pre-fills the checkout.
	const masterSelections: MasterSelection[] = batches.flatMap((batch) =>
		batch.savedSelection?.fromDownloadedQuote
			? batch.savedSelection.selections.map((s) => ({
					offerId: s.offerId,
					pricingId: s.pricingId,
					deliveryId: s.deliveryId ?? undefined,
					sampleChoice: s.sampleChoice ?? undefined,
				}))
			: []
	);

	// What each card currently has selected, reported live rather than read back
	// from the debounced saved selection — accepting can follow a change by less
	// than the save delay, and nothing refetches the batch in between.
	//
	// This is now the accept flow's only source of products, quantities, delivery
	// and plan: the modal asks for samples and invoice details, nothing else.
	const [selectionByBatch, setSelectionByBatch] = useState<Record<string, ClientBatchCardState>>(
		{}
	);
	const handleSelectionChange = (batchId: string, state: ClientBatchCardState) =>
		setSelectionByBatch((prev) => ({ ...prev, [batchId]: state }));

	const cardSelections = Object.values(selectionByBatch).flatMap((s) => s.selections);

	// One plan per batch, and accepting spans every batch — so this is a list.
	// Sending only the first would let a second batch's plan vanish silently.
	if (loading) {
		return (
			<div className="flex flex-col gap-4">
				{Array.from({ length: 2 }).map((_, i) => (
					<div
						key={i}
						className="h-[200px] animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface"
					/>
				))}
			</div>
		);
	}

	if (batches.length === 0) {
		return (
			<Card className="border-line-1 bg-surface py-16">
				<div className="flex flex-col items-center gap-3 text-center">
					<div className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
						<FileText size={20} />
					</div>
					<div className="text-base text-text-2">No offers yet</div>
					<div className="text-sm text-text-4">
						Offers will appear here once they’ve been sent to you.
					</div>
				</div>
			</Card>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			{batches.map((batch) => (
				<Fragment key={batch.id}>
					{renderBatch({
						batch,
						itemLookup,
						quoteDeadline,
						onSelectionChange: handleSelectionChange,
						onAccept: hasPendingOffers ? () => setShowAcceptModal(true) : undefined,
						onReject: setRejectTarget,
					})}
				</Fragment>
			))}

			{/* ── Reject batch modal ── */}
			<Dialog
				open={!!rejectTarget}
				onOpenChange={(open) => {
					if (!open) {
						setRejectTarget(null);
						setRejectReason('');
					}
				}}
			>
				<DialogContent className="sm:max-w-sm">
					<DialogForm
						onSubmit={async () => {
							if (!rejectTarget || !rejectReason.trim()) return;
							try {
								await rejectBatch({
									variables: {
										input: {
											batchId: rejectTarget.id,
											reason: rejectReason.trim(),
										},
									},
								});
								toast.success(`"${rejectTarget.name}" rejected`);
								setRejectTarget(null);
								setRejectReason('');
								refetch();
							} catch (err) {
								toast.error(
									err instanceof Error ? err.message : 'Failed to reject offer'
								);
							}
						}}
					>
						<DialogHeader className="p-4">
							<DialogTitle>Reject all offers</DialogTitle>
							<DialogDescription>
								Reject all offers in <strong>{rejectTarget?.name}</strong>. This
								cannot be undone.
							</DialogDescription>
						</DialogHeader>
						<DialogBody>
							<Field label="Reason" required>
								<Textarea
									value={rejectReason}
									onChange={(e) => setRejectReason(e.target.value)}
									placeholder="Why are you rejecting this offer?"
									rows={3}
									autoFocus
								/>
							</Field>
						</DialogBody>
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => {
									setRejectTarget(null);
									setRejectReason('');
								}}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								variant="destructive"
								disabled={!rejectReason.trim() || rejecting}
							>
								{rejecting ? 'Rejecting…' : 'Reject all offers'}
							</Button>
						</DialogFooter>
					</DialogForm>
				</DialogContent>
			</Dialog>

			{/* ── Accept offers modal ── */}
			<AcceptOffersModal
				open={showAcceptModal}
				onOpenChange={setShowAcceptModal}
				offers={allPendingOffers}
				masterSelections={cardSelections.length > 0 ? cardSelections : masterSelections}
				quoteContext={{
					quoteId,
					quoteName,
					quoteItems,
					deliveryAddress: quoteDeliveryAddress ?? null,
					organizationId,
					allBatches: batches.map((b) => ({
						id: b.id,
						offers: b.offers.map((o) => ({
							id: o.id,
							orderQuoteItemId: o.orderQuoteItemId,
							status: o.status,
						})),
					})),
				}}
				onSuccess={() => {
					refetch();
					onQuoteChanged?.();
				}}
			/>
		</div>
	);
}
